import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Sprout, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F9F8F6]">
      <div
        className="hidden md:block relative"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1765370817530-346a87971414?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwYWJzdHJhY3QlMjB3YXJtJTIwc2FuZCUyMHRleHR1cmV8ZW58MHx8fHwxNzc4MDgzMDIyfDA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1D3328]/40 via-transparent to-[#E07A5F]/20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="label-kicker text-white/80">Account recovery</p>
          <h2 className="font-display text-4xl font-medium mt-3 leading-tight">
            Lost your way?<br />Find it back.
          </h2>
          <p className="mt-4 text-sm text-white/80 max-w-sm">
            We&apos;ll email a one-time link valid for 30 minutes. Your debts and assets are waiting.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md fade-in">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#2C4A3B] flex items-center justify-center text-white">
              <Sprout className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div>
              <div className="font-display text-xl font-medium text-[#1D3328]">Quietus</div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8B9389]">Debt Tracker</div>
            </div>
          </div>

          {sent ? (
            <div className="space-y-5" data-testid="forgot-success">
              <div className="w-12 h-12 rounded-2xl bg-[#E5EFE7] border border-[#829A88]/30 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#2C4A3B]" strokeWidth={1.8} />
              </div>
              <p className="label-kicker">Check your inbox</p>
              <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A]">
                Email sent
              </h1>
              <p className="text-[#5C635A]">
                If an account exists for <span className="font-medium text-[#1D3328]">{email}</span>,
                we just sent a one-time reset link there. It expires in 30 minutes.
              </p>
              <p className="text-sm text-[#8B9389]">
                Don&apos;t see it? Check your spam folder, or{" "}
                <button
                  onClick={() => { setSent(false); setEmail(""); }}
                  className="text-[#2C4A3B] font-medium underline underline-offset-4"
                  data-testid="forgot-try-again"
                >
                  try another email
                </button>.
              </p>
              <Link
                to="/login"
                className="inline-block text-sm text-[#2C4A3B] font-medium underline underline-offset-4 mt-4"
                data-testid="forgot-back-to-login"
              >
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <p className="label-kicker">Reset access</p>
              <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
                Forgot password?
              </h1>
              <p className="mt-3 text-[#5C635A]">
                Enter your email and we&apos;ll send you a reset link.
              </p>

              <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1D3328] font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B9389]" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl border-[#E5E3DB] h-11 bg-white pl-9"
                      placeholder="you@example.com"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                {error && (
                  <div
                    className="text-sm text-[#B04A32] bg-[#F7E6DF] border border-[#E07A5F]/30 rounded-lg px-3 py-2"
                    data-testid="forgot-error"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white font-medium transition-all hover:-translate-y-0.5"
                  data-testid="forgot-submit-button"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </Button>
              </form>

              <p className="mt-6 text-sm text-[#5C635A]">
                Remembered it?{" "}
                <Link to="/login" className="text-[#2C4A3B] font-medium underline underline-offset-4" data-testid="forgot-to-login">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
